import {
    CompanyExtensionBillingCycleEnum,
    CompanyExtensionStatusEnum,
    PlanExtensionSourceEnum
} from "@dropDesk/domain/entities/plan_extensions/plan_extension.enum";
import {CompanyExtensionProps} from "@dropDesk/domain/entities/plan_extensions/company_extension.props";
import {PlanExtensionCatalogEntity} from "@dropDesk/domain/entities/plan_extensions/plan_extension_catalog.entity";
import {PaymentEntity} from "@dropDesk/domain/entities/payment/payment.entity";
import {diffInDaysBetweenDates, formatIsoDateToDDMMYYYY, resetHours} from "@dropDesk/utils/helpers/date_helper";
import {PaymentExtensionEntity} from "@dropDesk/domain/entities/plan_extensions/payment_extension.entity";

export class CompanyExtensionEntity {
    idCompany!: string;
    idExtension!: string;
    status!: CompanyExtensionStatusEnum;
    dayDue!: number;
    nextDueDate!: string;
    expirationDate?: string | null;
    isFree!: boolean;
    isCancelable!: boolean;
    nonCancelableReason?: string | null;
    unitPrice!: number;
    billingCycle?: CompanyExtensionBillingCycleEnum | null;
    contractedAt!: string;
    canceledAt?: string | null;
    cancelScheduledTo?: string | null;
    cancellationReason?: string | null;
    source!: PlanExtensionSourceEnum;
    deleted?: boolean;
    createdAt?: string;
    updatedAt?: string;
    extensionCatalog?: PlanExtensionCatalogEntity | null;
    payments?: PaymentExtensionEntity[];

    constructor(props: CompanyExtensionProps) {
        Object.assign(this, props);
    }

    public get nextDueDateDDMMYYYY(): string {
        const nextDueDate = resetHours(new Date(this.nextDueDate)).toISOString();
        return formatIsoDateToDDMMYYYY(nextDueDate);
    }

    get isActive(): boolean {
        return this.status === CompanyExtensionStatusEnum.active;
    }

    get isCanceled(): boolean {
        return this.status === CompanyExtensionStatusEnum.canceled;
    }

    get isActiveAndPayable(): boolean {
        return !this.isCanceled && !this.isFree;
    }

    get isSourceLegacy(): boolean {
        return this.source === PlanExtensionSourceEnum.legacy;
    }

    get isFreeNotCore(): boolean {
        return this.isFreeFromAccountMigrated && !this.extensionCatalog?.isCore;
    }

    get isFreeFromAccountMigrated(): boolean {
        return this.isFree && !this.isCancelable;
    }

    get daysToExpiration(): number {
        if (!this.expirationDate) {
            return 0;
        }
        return Math.ceil(diffInDaysBetweenDates(new Date(this.expirationDate), new Date()));
    }

    get isScheduledCancel(): boolean {
        return this.status === CompanyExtensionStatusEnum.scheduledCancel;
    }

    get isWaitingPayment(): boolean {
        return this.status === CompanyExtensionStatusEnum.waitingPayment;
    }

    get isPaymentRequired(): boolean {
        return this.status === CompanyExtensionStatusEnum.paymentRequired;
    }

    get isWaitingPaymentOrPaymentRequired(): boolean {
        return this.isWaitingPayment || this.isPaymentRequired;
    }

    public get cancelScheduledToDDMMYYYY(): string | undefined {
        if (this.cancelScheduledTo) {
            return formatIsoDateToDDMMYYYY(resetHours(new Date(this.cancelScheduledTo)).toISOString());
        }
        return undefined;
    }

    public get lastPaymentExpireDateDDMMYYYY(): string | undefined {
        if (this.lastPayment?.expireDate) {
            return formatIsoDateToDDMMYYYY(resetHours(new Date(this.lastPayment.expireDate)).toISOString());
        }
        return undefined;
    }

    public get lastPayment(): PaymentEntity | undefined {
        const data = (this.payments ?? []).filter(entry => !!entry.payment).map(entry => entry.payment!);
        const payments = this.orderPaymentsByCreatedAt(data);
        return payments.length ? payments[payments.length - 1] : undefined;
    }

    public orderPaymentsByCreatedAt(payments?: PaymentEntity[]): PaymentEntity[] {
        return payments ? payments.sort((a, b) => new Date(a.createdAt!).getTime() - new Date(b.createdAt!).getTime()) : [];
    }


    static fromJson(json: Record<string, any>): CompanyExtensionEntity {
        return new CompanyExtensionEntity({
            idCompany: json['idCompany'] as string,
            idExtension: json['idExtension'] as string,
            status: json['status'] as CompanyExtensionStatusEnum,
            dayDue: json['dayDue'] as number,
            nextDueDate: json['nextDueDate'] as string,
            expirationDate: json['expirationDate'] ?? null,
            isFree: (json['isFree'] as boolean) ?? false,
            isCancelable: (json['isCancelable'] as boolean) ?? false,
            nonCancelableReason: json['nonCancelableReason'] ?? null,
            unitPrice: json['unitPrice'] as number,
            billingCycle: (json['billingCycle'] as CompanyExtensionBillingCycleEnum | null) ?? null,
            contractedAt: json['contractedAt'] as string,
            canceledAt: json['canceledAt'] ?? null,
            cancelScheduledTo: json['cancelScheduledTo'] ?? null,
            cancellationReason: json['cancellationReason'] ?? null,
            source: json['source'] as PlanExtensionSourceEnum,
            deleted: (json['deleted'] as boolean | null) ?? false,
            createdAt: json['createdAt'] ?? null,
            updatedAt: json['updatedAt'] ?? null,
            payments: json['payments']
                ? (json['payments'] as any[]).map((entry) =>
                    PaymentExtensionEntity.fromJson(entry),
                )
                : [],
            extensionCatalog: json['extensionCatalog']
                ? PlanExtensionCatalogEntity.fromJson(json['extensionCatalog'])
                : null
        });
    }
}
